import { useState } from "react";
import { Sparkles, Bot, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useWorkDocumentVersions, useSaveDocumentVersion, useRestoreDocumentVersion, downloadVersionPdf } from "@/lib/workDocumentQueries";
import { flushAllPendingBlockSaves } from "@/hooks/useBlockLease";
import PromptDialog from "../presentation/PromptDialog";
import ConfirmDialog from "../presentation/ConfirmDialog";

export default function WorkDocumentVersionPanel({ workspaceId, docId, canEdit, hasSourceFile, documentTitle }) {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingRestoreId, setPendingRestoreId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const { data } = useWorkDocumentVersions(workspaceId, docId);
  const saveVersion = useSaveDocumentVersion(workspaceId, docId);
  const restoreVersion = useRestoreDocumentVersion(workspaceId, docId);
  const versions = data?.items || [];

  async function handleSaveAsVersion(label) {
    // A block's own 800ms autosave debounce (or an in-flight save) may
    // not have reached the server yet — flush every block NOW so the
    // snapshot this version takes is exactly what's on screen, never a
    // stale/empty pre-edit value.
    await flushAllPendingBlockSaves(docId);
    saveVersion.mutate(label);
  }

  async function handleRestore(versionId) {
    setPendingRestoreId(null);
    // Same race as "Save as version" — flush first so the automatic
    // pre-restore checkpoint captures your latest edits, not a stale value.
    await flushAllPendingBlockSaves(docId);
    restoreVersion.mutate(versionId);
  }

  async function handleDownload(version) {
    setDownloadingId(version.version_id);
    try {
      await downloadVersionPdf(workspaceId, docId, version.version_id, `${documentTitle || "document"} — ${version.label}.pdf`);
    } catch (e) {
      toast.error(e.status === 422 ? "This document wasn't created from an uploaded file, so there's no original template to fill in." : "Couldn't generate the PDF. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="border-t border-ink/10 pt-5 mt-6" data-testid="work-document-version-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-lg text-ink">Version History</h2>
        {canEdit && (
          <button onClick={() => setSaveDialogOpen(true)} className="text-sm font-medium text-terracotta hover:text-terracotta/80" data-testid="work-document-version-save-as">
            Save as version
          </button>
        )}
      </div>
      <p className="text-xs text-ink-soft mb-3">
        InterAct always checkpoints the current state first before a restore — no version is ever deleted.
      </p>
      <div className="space-y-2" data-testid="work-document-version-list">
        {versions.map((v) => (
          <div key={v.version_id} className="flex items-center justify-between gap-3 border border-ink/15 bg-white/50 rounded-lg px-4 py-3" data-testid={`work-document-version-${v.version_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {v.kind === "manual" ? <Sparkles size={15} className="text-terracotta shrink-0" /> : <Bot size={15} className="text-ink-soft shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm text-ink truncate">{v.label}</p>
                <p className="text-xs text-ink-soft">#{v.sequence_number} · {v.kind}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {hasSourceFile && (
                <button
                  onClick={() => handleDownload(v)} disabled={downloadingId === v.version_id}
                  className="flex items-center gap-1 text-xs text-ink-soft hover:text-terracotta disabled:opacity-50"
                  data-testid={`work-document-version-${v.version_id}-download`}
                  title="Download this version as a PDF of your original document with your answers filled in"
                >
                  {downloadingId === v.version_id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                  Download
                </button>
              )}
              {canEdit && (
                <button onClick={() => setPendingRestoreId(v.version_id)} className="text-xs text-ink-soft hover:text-terracotta" data-testid={`work-document-version-${v.version_id}-restore`}>
                  Restore
                </button>
              )}
            </div>
          </div>
        ))}
        {versions.length === 0 && <p className="text-ink-soft text-sm" data-testid="work-document-version-empty">No versions saved yet.</p>}
      </div>
      <PromptDialog
        open={saveDialogOpen} onOpenChange={setSaveDialogOpen} title="Save as version" placeholder="e.g. First full draft"
        confirmLabel="Save" onConfirm={handleSaveAsVersion} testId="work-document-version-save-dialog"
      />
      <ConfirmDialog
        open={!!pendingRestoreId} onOpenChange={(o) => !o && setPendingRestoreId(null)}
        title="Restore this version?" confirmLabel="Restore"
        description="InterAct saves the current state as an automatic checkpoint first, so you can always come back — no version is ever deleted."
        onConfirm={() => handleRestore(pendingRestoreId)}
        testId="work-document-version-restore-confirm"
      />
    </div>
  );
}
